import { Image } from "@client/content/controls";
import Icon from "@client/controls/Icon/component";
import NumberInput from "@client/controls/Inputs/NumberInput/component";
import Link from "@client/controls/Link/container";
import { serializeValue } from "@client/lib/values/size";
import { Dimension, IconType } from "@client/types";
import { Grid } from "@common/controls/Grid/component";
import { type GridColumn } from "@common/controls/Grid/types";
import { useI18n } from "@common/hooks/i18n";
import classNames from "classnames";
import { isNull } from "lodash";
import * as React from "react";
import ProductPrice from "../../ProductList/Content/Item/ProductPrice";
import { type Cart } from "../types";

import "./style.less";

type CartItemProps = {
  addon?: any;
  availableQuantity: number | null;
  eventIsAvailable: boolean;

  imageSize: string;
  imageUrl: string | null;
  isNameHidden?: boolean;
  name: string;
  noImageUrl: string;
  permalink: string | null;
  quantity: number;
  subname: string | null;
};
export const CartItem = (props: CartItemProps) => {
  const i18n = useI18n();

  return (
    <Link className="wsc_cart_item_info" to={props.permalink}>
      <Image size={props.imageSize} src={props.imageUrl ?? props.noImageUrl} isFixedSize />

      <div className="name">
        {!props.isNameHidden && <div className="main">{props.name}</div>}
        {!!props.subname && <div className={props.isNameHidden ? "main" : "sub"}>{props.subname}</div>}
        {!props.eventIsAvailable ? (
          <div className="unavailable">{i18n.infoProductUnavailableForEvent}</div>
        ) : (props.availableQuantity ?? 0) > props.quantity ? null : isNull(props.availableQuantity) ? (
          <div className="unavailable">{i18n.infoProductUnavailable}</div>
        ) : !props.availableQuantity ? (
          <div className="unavailable">{i18n.infoOutOfStock}</div>
        ) : (
          <div className="partially_available">
            {props.availableQuantity} {i18n.available.toLowerCase()}
          </div>
        )}
        {props.addon}
      </div>
    </Link>
  );
};

type CartItemPriceProps = {
  currencyCode: string;
  item: Cart.CartItem;
  isDisabled?: boolean;
  /**
   * @default "quantity"
   */
  mode?: "quantity" | "total";
  shouldDisplayTax: boolean;

  onDelete?: (id: string) => void;
};
export const CartItemPrice = ({ item, mode = "quantity", ...props }: CartItemPriceProps) => (
  <div className={classNames("amount_wrap", { disabled: props.isDisabled })}>
    {mode === "quantity" && <span>{item.quantity} x </span>}
    <ProductPrice
      price={{
        currencyCode: props.currencyCode,
        price: mode === "quantity" ? item.originalPrice : item.originalPrice * item.quantity,
        salesPrice: mode === "quantity" ? item.price : item.price * item.quantity
      }}
      salePriceStyle={{
        textDecoration: "lline-through"
      }}
      taxPercentage={props.shouldDisplayTax ? item.taxPercentage : 0}
    />

    {props.onDelete && (
      <Icon iconClassName={IconType.Delete} onClick={() => !props.isDisabled && props.onDelete!(item.id)} />
    )}
  </div>
);

type Props = {
  fieldStyle?: React.CSSProperties;
  isBusy?: boolean;
  items: Cart.CartItem[];
  imageSize: number;
  currencyCode: string;
  isOffer?: boolean;
  noImageUrl: string;
  redirectOnClick: boolean;
  shouldDisplayTax: boolean;

  onDelete?: (id: string) => void;
  onQuantityChange?: (id: string, value: number) => void;
};
const CartItems = (props: Props) => {
  const i18n = useI18n();

  const imageSize = React.useMemo(
    () =>
      serializeValue({
        dimension: Dimension.Height,
        dimensionSize: props.imageSize,
        isLocked: true,
        height: props.imageSize,
        width: props.imageSize,
        useAspectRatio: false
      }),
    [props.imageSize]
  );

  const columns = React.useMemo((): GridColumn<Cart.CartItem>[] => {
    const result: GridColumn<Cart.CartItem>[] = [
      {
        align: "left",
        key: "name",
        name: props.isOffer ? "" : i18n.product,
        dataType: "string",
        data: (x) => (
          <CartItem
            eventIsAvailable={true}
            imageSize={imageSize}
            noImageUrl={props.noImageUrl}
            {...x}
            permalink={props.redirectOnClick ? x.permalink : null}
          />
        ),
        width: "99%"
      }
    ];

    if (!props.isOffer) {
      result.push({
        align: "left",
        key: "quantity",
        name: i18n.quantity,
        dataType: "number",
        data: (x) =>
          props.onQuantityChange ? (
            <NumberInput
              className="quantity"
              isDisabled={isNull(x.availableQuantity)}
              onChange={(value) => props.onQuantityChange!(x.id, value)}
              style={props.fieldStyle}
              value={x.quantity}
              min={1}
              max={x.availableQuantity}
            />
          ) : (
            x.quantity
          )
      });

      result.push({
        align: "right",
        key: "price",
        name: i18n.amount,
        dataType: "number",
        data: (x) => (
          <CartItemPrice
            item={x}
            isDisabled={props.isBusy}
            currencyCode={props.currencyCode}
            mode="total"
            onDelete={props.onDelete}
            shouldDisplayTax={props.shouldDisplayTax}
          />
        )
      });
    }

    return result;
  }, [
    props.isBusy,
    props.redirectOnClick,
    props.fieldStyle,
    props.currencyCode,
    props.isOffer,
    props.onDelete,
    props.onQuantityChange,
    props.shouldDisplayTax
  ]);

  return <Grid idKey="id" columns={columns} data={props.items} isLoading={false} />;
};

export default CartItems;
